import { useCallback, useEffect, useState } from 'react';
import { PlaybookDto, playbookService } from '../services/playbookService';
import { Playbook } from '../types';

export interface PlaybooksState {
  playbooks: Playbook[];
  isLoading: boolean;
  error: string | null;
}

export const usePlaybooks = () => {
  const [state, setState] = useState<PlaybooksState>({
    playbooks: [],
    isLoading: false,
    error: null,
  });

  const mapDtoToPlaybook = useCallback(
    (dto: PlaybookDto): Playbook => ({
      id: dto.playbookId,
      name: dto.name,
      description: dto.description,
      entityType: dto.entityType,
      owner: dto.createdByUserId || 'System',
      lastUpdate: dto.updatedAt || dto.createdAt || new Date().toISOString(),
      runs: 0,
      failedSuccess: '0/0',
      status: dto.enabled ? 'Active' : 'Inactive',
      actions: dto.actions,
    }),
    []
  );

  const fetchPlaybooks = useCallback(async () => {
    if (state.playbooks.length > 0 || state.isLoading) return;

    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const data = await playbookService.getPlaybooks();
      setState(prev => ({
        ...prev,
        playbooks: Array.isArray(data) ? data.map(mapDtoToPlaybook) : [],
        isLoading: false,
      }));
    } catch (error) {
      console.error('Failed to fetch playbooks', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load playbooks. Please try again later.',
      }));
    }
  }, [mapDtoToPlaybook]);

  useEffect(() => {
    fetchPlaybooks();
  }, [fetchPlaybooks]);

  const deletePlaybook = useCallback(async (id: string) => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      await playbookService.deletePlaybook(id);
      setState(prev => ({
        ...prev,
        playbooks: prev.playbooks.filter(p => p.id !== id),
        isLoading: false,
      }));
    } catch (error) {
      console.error('Failed to delete playbook', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const addPlaybook = useCallback((playbook: Omit<Playbook, 'lastUpdate' | 'runs' | 'failedSuccess'>) => {
    const newPlaybook: Playbook = {
      ...playbook,
      lastUpdate: new Date().toISOString(),
      runs: 0,
      failedSuccess: '0/0',
    };
    setState(prev => ({
      ...prev,
      playbooks: [newPlaybook, ...prev.playbooks],
    }));
  }, []);

  const updatePlaybook = useCallback((id: string, updates: Partial<Playbook>) => {
    setState(prev => ({
      ...prev,
      playbooks: prev.playbooks.map(p => (p.id === id ? { ...p, ...updates, lastUpdate: new Date().toISOString() } : p)),
    }));
  }, []);

  return {
    ...state,
    fetchPlaybooks,
    deletePlaybook,
    addPlaybook,
    updatePlaybook,
  };
};
